import { getPreviewCard } from 'api/iframely'
import classnames from 'classnames'
import Retry from 'components/lib/icons/Retry'
import NakedButton from 'components/lib/NakedButton'
import MessageFooter from 'components/MessageFooter'
import store from 'config/store'
import useThemeColors from 'hooks/useThemeColors'
import { sendMessage } from 'modules/conversations/actions'
import DateTimeManager from 'modules/dateTime'
import { hasMessageAttribute, isEndUserMessage } from 'modules/message/utils'
import { autoScrollChatToBottom } from 'modules/view/actions'
import { pathOr } from 'ramda'
import React, { useEffect, useLayoutEffect, useRef,useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Message, MessageContentType, MessageSenderType, MessageType } from 'types/message'
import { log } from 'utils'
import processGifsForAnimationControl from 'utils/gifAnimationControl'
import { getUrlStringsFromText } from 'utils/url'

import { resolveMessageComponent } from './messageTypes'
import AttachmentMessage from './messageTypes/attachment'
import UnfurlMessage from './messageTypes/unfurl'

import './style.css'

interface MessageProps {
  message: Message,
  nextMessage?: Message,
  isLastGroup: boolean,
  isLastMessage: boolean,
  resolveMessageHeight: () => void
}


const MessageSentTimestamp = ({ sender, deliveredAt }) => {
  const { t } = useTranslation()

  return <span className={`drift-widget-message-timestamp drift-widget-message-timestamp--${sender}`}>
            { DateTimeManager.unixFormatDateTime(deliveredAt, t('dt.format.time.short'))}
          </span>
}

const MessageComponent = ({
  message,
  nextMessage,
  isLastGroup,
  isLastMessage,
  resolveMessageHeight
}: MessageProps) => {
  const [previewUrls, setUrlsToPreview] = useState([])
  const [unfurl, setUnfurl] = useState(null)
  const dispatch = useDispatch()
  const messageWrapperRef = useRef()

  const shouldShowUnfurls = useSelector(state => (
    pathOr(false, ['embed', 'configuration', 'theme', 'showUnfurl'], state)
  ))

  const doSendMessage = () => {
    dispatch(sendMessage({
      message: {
        ...message,
        attributes: {
          ...message.attributes,
          generatedAt: new Date().valueOf()
        }
      }
    }))
  }

  const { borderStyles, endUserMessageTheme, agentMessageTheme } = useThemeColors()

  const ContentComponent = resolveMessageComponent(message)
  const sender = isEndUserMessage(message)
    ? MessageSenderType.END_USER
    : MessageSenderType.AGENT

  useEffect(() => {
    // WCAG WID-996
    // Gives users the ability to play/pause GIF animations
    processGifsForAnimationControl(messageWrapperRef?.current)
  }, [messageWrapperRef])

  useEffect(() => {
    if (hasMessageAttribute(message, 'instantMeeting')) {
      return
    }

    const urlsInMessage = getUrlStringsFromText(message.body)
    urlsInMessage.length && setUrlsToPreview(urlsInMessage)
  }, [message])

  useEffect(() => {
    if (previewUrls.length > 0) {
      const txt = document.createElement("textarea")
      txt.innerHTML = previewUrls[0]

      getPreviewCard(txt.value.replace("#61;", ""))
        .then(({ data }) => {
          setUnfurl({
            ...data,
            url: previewUrls[0]
          })
        })
        .catch((err) => log({ data: [`Failed to unfurl url: ${previewUrls[0]}`, err] }))
    }
  }, [previewUrls])

  useLayoutEffect(() => {
    resolveMessageHeight()
    store.dispatch(autoScrollChatToBottom(true))
  }, [resolveMessageHeight, isLastGroup])

  const isCardContent = (
    !!message.attributes.appointmentInfo ||
    !!message.attributes.conversationRating ||
    message.type === MessageType.EMAIL_CAPTURE_CARD ||
    message.contentType === MessageContentType.SMS_NUMBER_REQUESTED ||
    message.contentType === MessageContentType.SMS_NUMBER_SUCCESS
  )
  const shouldRenderMessage = !!message.body || isCardContent
  const shouldRenderUnfurls = shouldShowUnfurls && unfurl
  const shouldRenderAttachments = message.attachments && message.attachments.length > 0

  if (!shouldRenderMessage && !shouldRenderUnfurls && !shouldRenderAttachments) {
    return null
  }

  const delivered = pathOr(null, ['createdAt'])(message)
  const generated = pathOr(null, ['attributes', 'generatedAt'])(message)
  const messageTimedOut = !delivered //lets not calc this if we dont have to
    ? DateTimeManager.isTimeAfterSeconds(DateTimeManager.getCurrentTimeEpoch(), generated, 30)
    : false

  const deliveryFailed = !delivered && messageTimedOut

  return (
    <>
      {shouldRenderMessage &&
        <li
          className={classnames(`drift-widget-message drift-widget-message-sender--${sender}`, {
          'drift-widget-message-failed': deliveryFailed
          })}
          ref={messageWrapperRef}
        >
          <ContentComponent
            {...message}
            isLastGroup={isLastGroup}
            isLastMessage={isLastMessage}
            nextMessage={nextMessage}
            resolveMessageHeight={resolveMessageHeight}
            style={sender === MessageSenderType.END_USER ? endUserMessageTheme : agentMessageTheme}
          />

          {!isCardContent && delivered &&
            <MessageSentTimestamp sender={sender} deliveredAt={delivered} />
          }
          {deliveryFailed &&
            <NakedButton onClick={doSendMessage}>
              <Retry height={12} width={12} />
            </NakedButton>
          }
        </li>
      }

      {shouldRenderUnfurls &&
        <UnfurlMessage
          unfurlData={unfurl}
          resolveMessageHeight={resolveMessageHeight}
          style={sender === MessageSenderType.END_USER ? { ...borderStyles } : {}}
          sender={sender}
        />
      }

      {shouldRenderAttachments &&
        <li className={`drift-widget-message drift-widget-message-sender--${sender}`}>
          <AttachmentMessage
            attachmentIds={message.attachments}
            resolveMessageHeight={resolveMessageHeight}
            sentFrom={sender}
          />
          <MessageSentTimestamp sender={sender} deliveredAt={delivered} />
        </li>
      }

      <MessageFooter
        message={message}
        isLastGroup={isLastGroup}
        isLastMessage={isLastMessage}
        delivered={delivered}
        generated={generated}
        messageTimedOut={messageTimedOut}
        deliveryFailed={deliveryFailed}
        resolveMessageHeight={resolveMessageHeight}
      />
    </>
  )
}


export default MessageComponent
